import React, { useState, useMemo } from "react";
import { Sheet, Button, Spinner } from "@ui";
import { useDocuments, formatFileSize, type Document } from "@agentic_system/api/document";
import { Check, FileText, Image, Table, File, Search, X } from "lucide-react";

interface DocumentSelectorDrawerProps {
  open: boolean;
  onClose: () => void;
  onAdd: (documents: Document[]) => void;
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith("image/")) return Image;
  if (contentType === "application/pdf") return FileText;
  if (contentType.includes("wordprocessingml")) return FileText;
  if (contentType.includes("spreadsheetml")) return Table;
  return File;
}

export function DocumentSelectorDrawer({
  open,
  onClose,
  onAdd,
}: DocumentSelectorDrawerProps) {
  const [search, setSearch] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>([]);
  const { data, isLoading } = useDocuments(1);

  // Reset local selection when drawer opens - always start fresh
  React.useEffect(() => {
    if (open) {
      setLocalSelectedIds([]);
      setSearch("");
    }
  }, [open]);

  const filteredDocuments = useMemo(() => {
    if (!data?.documents) return [];
    if (!search.trim()) return data.documents;

    const q = search.toLowerCase();
    return data.documents.filter((doc) =>
      doc.filename.toLowerCase().includes(q)
    );
  }, [data?.documents, search]);

  const toggleDocument = (id: number) => {
    setLocalSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    const selectedDocs = data?.documents.filter((d) =>
      localSelectedIds.includes(d.id)
    ) ?? [];
    onAdd(selectedDocs);
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Ajouter des documents"
      width="lg"
      footer={
        <div className="flex justify-between items-center w-full">
          <span className="text-sm text-slate-500">
            {localSelectedIds.length} document(s) sélectionné(s)
          </span>
          <div className="flex gap-3">
            <Button variant="white" onClick={onClose}>
              Annuler
            </Button>
            <Button onClick={handleConfirm} disabled={localSelectedIds.length === 0}>
              Ajouter
            </Button>
          </div>
        </div>
      }
    >
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un document..."
            className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-default-500"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Documents list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          {search ? "Aucun document trouvé" : "Aucun document disponible"}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
          {filteredDocuments.map((doc) => {
            const isSelected = localSelectedIds.includes(doc.id);
            const isOcrCompleted = doc.ocr_status === "completed";
            const isDisabled = !isOcrCompleted;
            const Icon = getFileIcon(doc.content_type);

            return (
              <div
                key={doc.id}
                className={`flex items-center gap-3 p-3 ${
                  isDisabled
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-50 cursor-pointer"
                }`}
                onClick={() => !isDisabled && toggleDocument(doc.id)}
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isSelected}
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) toggleDocument(doc.id);
                  }}
                  className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSelected
                      ? "bg-default-600 border-default-600 text-white"
                      : isDisabled
                        ? "border-slate-200 bg-slate-100"
                        : "border-slate-300 bg-white"
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                </button>

                {/* Thumbnail or icon */}
                <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {doc.thumbnail_url ? (
                    <img
                      src={doc.thumbnail_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon className="w-5 h-5 text-slate-400" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-slate-800 truncate">
                    {doc.filename}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatFileSize(doc.byte_size)}
                    {isOcrCompleted ? (
                      <span className="ml-2 text-green-600">OCR</span>
                    ) : (
                      <span className="ml-2 text-amber-600">
                        {doc.ocr_status === "pending" && "OCR en attente"}
                        {doc.ocr_status === "processing" && "OCR en cours..."}
                        {doc.ocr_status === "failed" && "OCR échoué"}
                        {doc.ocr_status === "skipped" && "OCR ignoré"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}
